// components/analytics/components/charts/BarChart.tsx
import { Bar } from '@antv/g2plot';
import { useEffect, useRef } from 'react';
import { Skeleton } from 'antd';
import NoDataPlaceholder from '../NoDataPlaceholder';

interface BarChartProps {
  data: Array<{
    [key: string]: any;
  }>;
  xField: string;
  yField: string;
  direction?: 'horizontal' | 'vertical';
  height?: number;
  color?: string;
  label?: boolean;
  loading?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  xField,
  yField,
  direction = 'vertical',
  height = 300,
  color = '#1890ff',
  label = true,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Bar | null>(null);

  useEffect(() => {
    // Strong null check for container
    if (!containerRef.current) {
      return;
    }

    // Cleanup previous instance
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Ensure data exists
    if (!data || data.length === 0) {
      return;
    }

    try {
      const chart = new Bar(containerRef.current, {
        data,
        xField: direction === 'horizontal' ? yField : xField,
        yField: direction === 'horizontal' ? xField : yField,
        isStack: true,
        isGroup: false,
        ...(direction === 'horizontal' ? {
          coordinate: [{ type: 'transpose' }]  // Corrección aquí
        } : {})
      });

      chartRef.current = chart;
      chart.render();
    } catch (error) {
      console.error('Failed to initialize or render chart:', error);
    }

    // Cleanup function
    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
          chartRef.current = null;
        } catch (error) {
          console.error('Failed to destroy chart:', error);
        }
      }
    };
  }, [data, xField, yField, direction, color, label]);

  // Show skeleton while loading
  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <NoDataPlaceholder height={height} />;
  }

  // Add wrapper div to ensure stable mounting
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <div ref={containerRef} style={{ height, width: '100%' }} />
    </div>
  );
};